import React, { useState, useRef, useEffect } from "react";
import {
  UploadCloud,
  FileText,
  AlertCircle,
  CheckCircle,
  Edit3,
  X,
  Plus,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import IdeasPageHeader from "./IdeasPageHeader";

const MAX_FILE_SIZE_MB = 10;
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
];

const AutoScribeComponent = ({ setActiveTab }) => {
  const [formData, setFormData] = useState({
    title: "",
    summary: "",
    businessCase: "",
    keyPoints: [],
    priority: "",
    department: "",
    estimatedCost: "",
    expectedROI: "",
    timeline: "",
    stakeholders: "",
    risks: "",
    technicalRequirements: "",
    successMetrics: "",
    isDocumentPopulated: false,
    id: "0",
    currentState: "",
    futureState: "",
    submittedBy: "",
  });
  const [showToast, setShowToast] = useState(false);
  const [activeReviewTab, setActiveReviewTab] = useState("review");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [newKeyPoint, setNewKeyPoint] = useState("");
  const [aiLoadingField, setAiLoadingField] = useState(null);
  const [userSelection, setUserSelection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionResponses, setQuestionResponses] = useState({});
  const [showVisionModal, setShowVisionModal] = useState(false);
  const fileInputRef = useRef(null);

  const priorities = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
  ];

  const departments = [
    { value: "IT", label: "Information Technology" },
    { value: "HR", label: "Human Resources" },
    { value: "Finance", label: "Finance" },
    { value: "Operations", label: "Operations" },
    { value: "Marketing", label: "Marketing" },
    { value: "Sales", label: "Sales" },
    { value: "Customer Service", label: "Customer Service" },
  ];

  const reviewerCriteria = {
    'Strategic Alignment': 'Mentions of mission alignment, TRM, OMB M-24-10, strategic goals.',
    'Value & Impact': 'Cost savings, efficiency, compliance improvements, risk reduction statements.',
    'Feasibility & Technical Readiness': 'Technical feasibility, tool/platform readiness, realistic implementation path.',
    'Risk & Compliance': 'References to privacy, civil rights, agency policies, NIST AI RMF 1.0, EO 14110, FedRAMP.',
    'Innovation & Modernization': 'Use of automation, AI/ML, new digital capabilities.',
    'Cost Consideration': 'Cost-benefit statements, use of existing investments.',
    'User & Stakeholder Impact': 'Positive end-user impacts, stakeholder considerations, accessibility.',
    'Governance & Oversight Readiness': 'Mentions of risk management, governance bodies, triage frameworks.',
    'Scalability & Reusability': 'Potential to scale or reuse across programs/organizations.',
    'Dependencies & Constraints': 'Data dependencies, system reliance, technical/operational limitations.'
  };

  const itpmReviewCriteria = {
    'Project Scope Definition': 'Clear definition of project boundaries, deliverables, and success criteria.',
    'Resource Allocation': 'Adequate staffing, budget allocation, and timeline considerations.',
    'Technical Architecture': 'System design, integration points, and technical infrastructure requirements.',
    'Stakeholder Engagement': 'Identification and involvement of key stakeholders throughout the project lifecycle.',
    'Quality Assurance': 'Testing strategies, quality gates, and validation processes.',
    'Change Management': 'Process for handling scope changes, impact assessment, and approval workflows.',
    'Documentation Standards': 'Required documentation, templates, and knowledge management practices.',
    'Performance Metrics': 'Key performance indicators, success metrics, and monitoring frameworks.',
    'Risk Mitigation': 'Risk identification, mitigation strategies, and contingency planning.',
    'Compliance Verification': 'Adherence to organizational policies, regulatory requirements, and industry standards.'
  };

  const fieldMapping = {
    title: "Title & Sponsor",
    summary: "Purpose",
    businessCase: "Proposed Business Solution",
    timeline: "Constraints/Requirements",
    stakeholders: "Impacted Stakeholders",
    risks: "Known Risks or Concerns",
    technicalRequirements: "Requested IT Actions",
    successMetrics: "Expected Benefits",
    currentState: "Current State",
    futureState: "Future State Vision",
    expectedROI: "Desired Outcome",
    estimatedCost: "Estimated Cost",
    department: "Department",
    priority: "Priority",
    submittedBy: "Submitted By",
  };

  // Fetch questions from API
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await fetch('/api/question/get_questions');
        if (!response.ok) {
          throw new Error('Failed to fetch questions');
        }
        const data = await response.json();
        
        // Handle different possible response formats
        let questionsArray = [];
        if (Array.isArray(data)) {
          questionsArray = data;
        } else if (data.questions && Array.isArray(data.questions)) {
          questionsArray = data.questions;
        } else if (data.Question) { // Handle case where response is an object with Question key
          questionsArray = [data.Question];
        } else if (typeof data === 'object') {
          questionsArray = Object.values(data);
        }

        setQuestions(questionsArray);
        
        // Initialize responses and expanded state
        
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    };

    fetchQuestions();
  }, []);

  const handleQuestionResponseChange = (questionKey, value) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionKey]: value
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.summary.trim()) newErrors.summary = "Summary is required";
    if (!formData.businessCase.trim())
      newErrors.businessCase = "Business case is required";
    if (!formData.priority) newErrors.priority = "Priority is required";
    if (!formData.department) newErrors.department = "Department is required";
    if (!formData.estimatedCost.trim())
      newErrors.estimatedCost = "Estimated cost is required";
    if (!formData.timeline.trim()) newErrors.timeline = "Timeline is required";
    if (!formData.stakeholders.trim())
      newErrors.stakeholders = "Stakeholders are required";

    if (uploadedFile) {
      if (!ALLOWED_FILE_TYPES.includes(uploadedFile.type)) {
        newErrors.file =
          "File type not supported. Allowed: PDF, DOC, DOCX, JPG, PNG.";
      }
      if (uploadedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        newErrors.file = `File size must be under ${MAX_FILE_SIZE_MB}MB.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      id: prev.isDocumentPopulated ? prev.id : "0"
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const UPLOAD_ENDPOINT = `/api/guidance/upload_draft`;
  const handleFileUpload = async (file) => {
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        file: "File type not supported. Allowed: PDF, DOC, DOCX, JPG, PNG.",
      }));
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        file: `File size must be under ${MAX_FILE_SIZE_MB}MB.`,
      }));
      return;
    }

    setUploadedFile(file);
    setIsProcessing(true);
    setProcessingStatus("Processing document...");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("file", file);

      const response = await fetch(UPLOAD_ENDPOINT, {
        method: "POST",
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error("Document processing failed");
      }

      const result = await response.json();

      // Debugging: Log the API response
      console.log("API Response:", result);

      if (result.data) {
        const apiData = result.data;

        // Use the fieldMapping to map API fields to form fields
        const mappedData = {};
        Object.entries(fieldMapping).forEach(([formField, apiField]) => {
          if (apiData[apiField] !== undefined) {
            mappedData[formField] = apiData[apiField];
          }
        });

        setFormData((prev) => ({
          ...prev,
          ...mappedData,
          keyPoints: apiData["Core Capabilities Required"]
            ? apiData["Core Capabilities Required"]
                .split(",")
                .map((item) => item.trim())
            : prev.keyPoints,
          isDocumentPopulated: true,
          id: apiData["Id"] || apiData["id"] || prev.id,
        }));
      }

      setProcessingStatus("Document processed successfully!");
      setTimeout(() => setProcessingStatus(""), 3000);
    } catch (error) {
      console.error("Error processing document:", error);
      setProcessingStatus("Error processing document");
      setTimeout(() => setProcessingStatus(""), 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
      e.target.value = ""; // Reset input to allow selecting same file again
    }
  };

  const addKeyPoint = () => {
    if (newKeyPoint.trim()) {
      setFormData((prev) => ({
        ...prev,
        keyPoints: [...prev.keyPoints, newKeyPoint.trim()],
        id: prev.isDocumentPopulated ? prev.id : "0"
      }));
      setNewKeyPoint("");
    }
  };

  const removeKeyPoint = (index) => {
    setFormData((prev) => ({
      ...prev,
      keyPoints: prev.keyPoints.filter((_, i) => i !== index),
    }));
  };

  const handleAIWrite = async (fieldName) => {
    setAiLoadingField(fieldName);
    setSubmissionStatus(null);

    try {
      const apiFieldName = fieldMapping[fieldName] || fieldName;

      const payload = {
        colunm_name: apiFieldName,
        text: formData[fieldName] || "",
        record: {
          "Title & Sponsor": formData.title,
          Purpose: formData.summary,
          "Desired Outcome": formData.expectedROI,
          "Current State": formData.currentState,
          "Future State Vision": formData.futureState,
          "Proposed Business Solution": formData.businessCase,
          "Core Capabilities Required": formData.keyPoints.join(", "),
          "Constraints/Requirements": formData.timeline,
          "Expected Benefits": formData.successMetrics,
          "Impacted Stakeholders": formData.stakeholders,
          "Known Risks or Concerns": formData.risks,
          "Requested IT Actions": formData.technicalRequirements,
          Attachments: uploadedFile ? uploadedFile.name : "",
          "Submitted By": formData.submittedBy,
          Id: formData.isDocumentPopulated ? formData.id : "0",
        },
      };

      const endpoint = `/api/guidance/improve`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const result = await response.json();
      const improvedText = result.improved_text || result.result;

      if (improvedText) {
        setFormData((prev) => ({
          ...prev,
          [fieldName]: improvedText,
        }));
        setSubmissionStatus(`AI successfully enhanced the ${fieldName} field!`);
      } else {
        throw new Error("Invalid response format from AI service");
      }
    } catch (error) {
      console.error("Error with AI writing:", error);
      setSubmissionStatus(`Error: ${error.message}`);
    } finally {
      setAiLoadingField(null);
      setTimeout(() => setSubmissionStatus(null), 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🚀 handleSubmit called", {
      isDocumentPopulated: formData.isDocumentPopulated,
      userSelection,
      setActiveTab: !!setActiveTab
    });

    if (!validate()) {
      console.log("❌ Validation failed");
      return;
    }

    setIsProcessing(true);
    setSubmissionStatus("Submitting idea and running AI reviews... This may take 1-2 minutes.");

    try {
      const payload = {
        "Title & Sponsor": formData.title,
        Purpose: formData.summary,
        "Desired Outcome": formData.expectedROI,
        "Current State": formData.currentState,
        "Future State Vision": formData.futureState,
        "Proposed Business Solution": formData.businessCase,
        "Core Capabilities Required": formData.keyPoints.join(", "),
        "Constraints/Requirements": formData.timeline,
        "Expected Benefits": formData.successMetrics,
        "Impacted Stakeholders": formData.stakeholders,
        "Known Risks or Concerns": formData.risks,
        "Requested IT Actions": formData.technicalRequirements,
        Attachments: uploadedFile ? uploadedFile.name : "",
        "Submitted By": formData.submittedBy,
        Id: formData.isDocumentPopulated ? formData.id : "0",
        question: questionResponses
      };

      const endpoint = `/api/guidance/finalize_submit`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      console.log("✅ Idea submitted successfully!", responseData);

      // Show toast notification
      setShowToast(true);
      setSubmissionStatus("Proposal submitted successfully!");

      // Navigate to Ideas Management after 2.5 seconds
      setTimeout(() => {
        console.log("🔄 Navigating to Ideas Management...", { setActiveTab });
        setShowToast(false); // Hide toast before navigation
        if (setActiveTab) {
          setActiveTab("ideas");
        } else {
          console.error("❌ setActiveTab is not defined!");
        }
      }, 2500);

    } catch (error) {
      console.error("Error submitting proposal:", error);
      setSubmissionStatus("Error submitting proposal. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const renderFieldLabel = (fieldName, label, required = false) => (
    <div className="flex items-center justify-between mb-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => handleAIWrite(fieldName)}
        className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center transition-colors duration-200 px-2 py-1 rounded-md hover:bg-blue-50"
        disabled={aiLoadingField === fieldName || isProcessing}
      >
        {aiLoadingField === fieldName ? (
          <>
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="w-3 h-3 mr-1" />
            Write with AI
          </>
        )}
      </button>
    </div>
  );

  const renderQuestionnaire = () => (
    <div style={{
      background: 'var(--cares-surface-card)',
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid var(--cares-line-card)',
      padding: '28px',
      height: 'fit-content'
    }}>
      <div style={{
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid var(--cares-line)'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '700',
          color: 'var(--cares-text-1)',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{
            width: '32px',
            height: '32px',
            background: 'linear-gradient(135deg, var(--cares-amber) 0%, var(--cares-amber) 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px'
          }}>
            📋
          </span>
          Assessment Questionnaire
        </h2>
        <p style={{
          fontSize: '13px',
          color: 'var(--cares-text-label)',
          marginLeft: '40px'
        }}>
          Help us understand your idea's alignment and impact
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {questions.map((question, index) => {
          // Get the question text in different possible formats
          const questionText = question.question || question.Question || question;
          const questionKey = question.id || questionText;
          const hasResponse = questionResponses[questionKey] && questionResponses[questionKey].trim().length > 0;

          return (
            <div
              key={index}
              style={{
                background: hasResponse
                  ? 'linear-gradient(135deg, color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card)) 0%, color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card)) 100%)'
                  : 'var(--cares-surface-card)',
                border: hasResponse ? '2px solid color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))' : '2px solid var(--cares-line-card)',
                borderRadius: '12px',
                padding: '20px',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <div style={{
                  minWidth: '28px',
                  height: '28px',
                  background: hasResponse
                    ? 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)'
                    : 'linear-gradient(135deg, var(--cares-amber) 0%, var(--cares-amber) 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  flexShrink: 0
                }}>
                  {hasResponse ? '✓' : '❓'}
                </div>
                <h3 style={{
                  fontSize: '15px',
                  fontWeight: '700',
                  color: 'var(--cares-text-1)',
                  lineHeight: '1.5',
                  margin: 0,
                  flex: 1
                }}>
                  {questionText}
                </h3>
              </div>

              <div style={{ position: 'relative' }}>
                <textarea
                  value={questionResponses[questionKey] || ''}
                  onChange={(e) => handleQuestionResponseChange(questionKey, e.target.value)}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid var(--cares-line-card)',
                    borderRadius: '10px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: 'var(--cares-text-1)',
                    background: 'var(--cares-surface-card)',
                    resize: 'vertical',
                    minHeight: '100px',
                    transition: 'all 0.2s',
                    fontFamily: 'inherit'
                  }}
                  placeholder="💭 Share your thoughts here..."
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--cares-amber)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--cares-line-card)';
                    e.target.style.boxShadow = 'none';
                  }}
                />

                {/* Character count */}
                <div style={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '12px',
                  fontSize: '11px',
                  color: 'var(--cares-text-note)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  pointerEvents: 'none'
                }}>
                  {(questionResponses[questionKey] || '').length} chars
                </div>
              </div>

              {/* Response status indicator */}
              {hasResponse && (
                <div style={{
                  marginTop: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  color: 'var(--cares-green)',
                  fontWeight: '600'
                }}>
                  <CheckCircle style={{ width: '14px', height: '14px' }} />
                  Response recorded
                </div>
              )}
            </div>
          );
        })}

        {/* Progress indicator */}
        {questions.length > 0 && (
          <div style={{
            marginTop: '8px',
            padding: '16px 20px',
            background: 'linear-gradient(135deg, color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card)) 0%, color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card)) 100%)',
            borderRadius: '12px',
            border: '2px solid color-mix(in srgb, var(--cares-blue) 30%, var(--cares-surface-card))'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <span style={{
                fontSize: '13px',
                fontWeight: '700',
                color: 'var(--cares-blue)'
              }}>
                📊 Progress
              </span>
              <span style={{
                fontSize: '13px',
                fontWeight: '700',
                color: 'var(--cares-blue)'
              }}>
                {Object.values(questionResponses).filter(r => r && r.trim().length > 0).length} / {questions.length}
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(Object.values(questionResponses).filter(r => r && r.trim().length > 0).length / questions.length) * 100}%`,
                height: '100%',
                background: 'linear-gradient(90deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
                transition: 'width 0.3s ease',
                borderRadius: '4px'
              }}></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderFormFields = () => (
    <div style={{
      background: 'var(--cares-surface-card)',
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid var(--cares-line-card)',
      padding: '32px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '32px',
        paddingBottom: '20px',
        borderBottom: '2px solid var(--cares-line)'
      }}>
        <div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: 'var(--cares-text-1)',
            marginBottom: '4px'
          }}>
            {userSelection === 'upload' ? '📝 Auto-Generated Draft' : '✍️ Proposal Details'}
          </h2>
          <p style={{
            fontSize: '14px',
            color: 'var(--cares-text-label)'
          }}>
            {userSelection === 'upload'
              ? 'Review and edit the AI-generated content below'
              : 'Fill in all required fields to submit your idea'}
          </p>
        </div>
        <Edit3 style={{ width: '24px', height: '24px', color: 'var(--cares-text-dim)' }} />
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Title */}
        <div>
          {renderFieldLabel("title", "Title", true)}
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Customer Portal Enhancement Initiative"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.title ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.title && (
            <p className="text-red-600 text-sm mt-1 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.title}
            </p>
          )}
        </div>

        {/* Summary */}
        <div>
          {renderFieldLabel("summary", "Summary", true)}
          <textarea
            name="summary"
            value={formData.summary}
            onChange={handleInputChange}
            rows={4}
            placeholder="Enhance the existing customer portal with improved dashboard functionality..."
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.summary ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.summary && (
            <p className="text-red-600 text-sm mt-1 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.summary}
            </p>
          )}
        </div>

        {/* Business Case */}
        <div>
          {renderFieldLabel("businessCase", "Business Case", true)}
          <textarea
            name="businessCase"
            value={formData.businessCase}
            onChange={handleInputChange}
            rows={4}
            placeholder="Current customer portal has a 45% bounce rate and low user engagement..."
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.businessCase ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.businessCase && (
            <p className="text-red-600 text-sm mt-1 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.businessCase}
            </p>
          )}
        </div>

        {/* Key Points */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Key Points
          </label>
          <div className="space-y-2">
            {formData.keyPoints.map((point, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
              >
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                  <span className="text-sm text-gray-700">{point}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeKeyPoint(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            <div className="flex space-x-2">
              <input
                type="text"
                value={newKeyPoint}
                onChange={(e) => setNewKeyPoint(e.target.value)}
                placeholder="Add a key point..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) =>
                  e.key === "Enter" &&
                  (e.preventDefault(), addKeyPoint())
                }
              />
              <button
                type="button"
                onClick={addKeyPoint}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Priority and Department */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority <span className="text-red-500">*</span>
            </label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.priority ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select priority</option>
              {priorities.map((priority) => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
            {errors.priority && (
              <p className="text-red-600 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.priority}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department <span className="text-red-500">*</span>
            </label>
            <select
              name="department"
              value={formData.department}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.department ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select department</option>
              {departments.map((dept) => (
                <option key={dept.value} value={dept.value}>
                  {dept.label}
                </option>
              ))}
            </select>
            {errors.department && (
              <p className="text-red-600 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.department}
              </p>
            )}
          </div>
        </div>

        {/* Estimated Cost and Expected ROI */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estimated Cost <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="estimatedCost"
              value={formData.estimatedCost}
              onChange={handleInputChange}
              placeholder="$50,000"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.estimatedCost
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
            {errors.estimatedCost && (
              <p className="text-red-600 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.estimatedCost}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expected ROI / Business Benefit
            </label>
            <textarea
              name="expectedROI"
              value={formData.expectedROI}
              onChange={handleInputChange}
              rows={4}
              placeholder="Examples:&#10;• Reduce operational costs by $500K annually through automation of manual processes&#10;• Increase revenue by $2M annually through improved customer conversion rates&#10;• Eliminate 2,000 hours of manual data entry per year, saving $150K in labor costs&#10;• Improve customer satisfaction from 72% to 90%, reducing churn by 15% and retaining $1.5M in ARR&#10;• Reduce compliance violations by 90%, avoiding potential fines of up to $1M annually"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Describe the financial return or business value with specific dollar amounts and timeframes
            </p>
          </div>
        </div>

        {/* Timeline and Stakeholders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timeline <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="timeline"
              value={formData.timeline}
              onChange={handleInputChange}
              placeholder="6 months"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.timeline ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.timeline && (
              <p className="text-red-600 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.timeline}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stakeholders <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="stakeholders"
              value={formData.stakeholders}
              onChange={handleInputChange}
              placeholder="Customer support team, IT department, end users"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.stakeholders
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
            {errors.stakeholders && (
              <p className="text-red-600 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.stakeholders}
              </p>
            )}
          </div>
        </div>

        {/* Current State and Future State */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {renderFieldLabel("currentState", "Current State")}
            <textarea
              name="currentState"
              value={formData.currentState}
              onChange={handleInputChange}
              rows={3}
              placeholder="Describe the current state of affairs..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            {renderFieldLabel("futureState", "Future State Vision")}
            <textarea
              name="futureState"
              value={formData.futureState}
              onChange={handleInputChange}
              rows={3}
              placeholder="Describe the desired future state..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Risks and Technical Requirements */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {renderFieldLabel("risks", "Known Risks or Concerns")}
            <textarea
              name="risks"
              value={formData.risks}
              onChange={handleInputChange}
              rows={3}
              placeholder="Potential risks and concerns..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            {renderFieldLabel(
              "technicalRequirements",
              "Requested IT Actions"
            )}
            <textarea
              name="technicalRequirements"
              value={formData.technicalRequirements}
              onChange={handleInputChange}
              rows={3}
              placeholder="Technical actions required..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Success Metrics and Submitted By */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {renderFieldLabel("successMetrics", "Expected Outcomes / Success Metrics")}
            <textarea
              name="successMetrics"
              value={formData.successMetrics}
              onChange={handleInputChange}
              rows={5}
              placeholder="Enter measurable success criteria, one per line:&#10;&#10;• Reduce processing time by 30%&#10;• Increase user satisfaction to 85%&#10;• Automate 50% of manual tasks&#10;• Decrease error rate from 5% to 1%&#10;• Improve system uptime to 99.9%&#10;• Reduce customer wait time by 2 minutes"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              List specific, measurable outcomes (one per line). Include baseline and target metrics where possible.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Submitted By
            </label>
            <input
              type="text"
              name="submittedBy"
              value={formData.submittedBy}
              onChange={handleInputChange}
              placeholder="Your name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Submission Status */}
        {submissionStatus && (
          <div
            className={`p-4 rounded-lg ${
              submissionStatus.includes("success")
                ? "bg-green-50 text-green-800"
                : submissionStatus.includes("Error")
                ? "bg-red-50 text-red-800"
                : "bg-blue-50 text-blue-800"
            }`}
          >
            <div className="flex items-center">
              {submissionStatus.includes("success") ? (
                <CheckCircle className="w-5 h-5 mr-2" />
              ) : submissionStatus.includes("Error") ? (
                <AlertCircle className="w-5 h-5 mr-2" />
              ) : (
                <div className="w-5 h-5 mr-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
              )}
              <span>{submissionStatus}</span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div style={{ paddingTop: '24px', borderTop: '2px solid var(--cares-line)' }}>
          <button
            type="submit"
            disabled={isProcessing}
            style={{
              width: '100%',
              background: isProcessing ? 'var(--cares-surface-raised)' : 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
              color: 'var(--cares-text-max)',
              fontWeight: '700',
              fontSize: '16px',
              padding: '16px 24px',
              borderRadius: '12px',
              border: 'none',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              boxShadow: isProcessing ? 'none' : '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!isProcessing) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isProcessing) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }
            }}
          >
            {isProcessing ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  borderTopColor: 'var(--cares-line-card)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Processing...
              </>
            ) : (
              <>
                <CheckCircle style={{ width: '20px', height: '20px' }} />
                Submit Proposal
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const renderUploadSection = () => (
    <div style={{
      background: 'var(--cares-surface-card)',
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid var(--cares-line-card)',
      padding: '32px'
    }}>
      <div style={{
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid var(--cares-line)'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: 'var(--cares-text-1)',
          marginBottom: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UploadCloud style={{ width: '24px', height: '24px', color: 'var(--cares-text-max)' }} />
          </span>
          Document Upload
        </h2>
        <p style={{
          fontSize: '14px',
          color: 'var(--cares-text-label)',
          marginLeft: '52px'
        }}>
          Upload your document and let AI extract the information
        </p>
      </div>

      <div
        style={{
          position: 'relative',
          border: dragActive ? '3px dashed var(--cares-blue)' : '3px dashed var(--cares-line-strong)',
          borderRadius: '20px',
          minHeight: '320px',
          background: dragActive
            ? 'linear-gradient(135deg, color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card)) 0%, color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card)) 100%)'
            : 'linear-gradient(135deg, var(--cares-surface-board) 0%, var(--cares-surface-board) 100%)',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          overflow: 'hidden'
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onMouseEnter={(e) => {
          if (!uploadedFile && !dragActive) {
            e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--cares-blue) 30%, var(--cares-surface-card))';
            e.currentTarget.style.background = 'linear-gradient(135deg, color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card)) 0%, color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card)) 100%)';
          }
        }}
        onMouseLeave={(e) => {
          if (!uploadedFile && !dragActive) {
            e.currentTarget.style.borderColor = 'var(--cares-line-strong)';
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--cares-surface-board) 0%, var(--cares-surface-board) 100%)';
          }
        }}
      >
        {/* Decorative background pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: 'radial-gradient(circle, var(--cares-blue) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          pointerEvents: 'none'
        }}></div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '320px',
          padding: '32px',
          position: 'relative',
          zIndex: 1
        }}>
          {uploadedFile ? (
            <div style={{ textAlign: 'center', width: '100%' }}>
              {/* Success State */}
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                animation: 'scaleIn 0.3s ease-out'
              }}>
                <CheckCircle style={{ width: '40px', height: '40px', color: 'var(--cares-text-max)' }} />
              </div>

              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: 'var(--cares-text-1)',
                marginBottom: '8px'
              }}>
                File Uploaded Successfully!
              </h3>

              <div style={{
                background: 'var(--cares-surface-card)',
                borderRadius: '12px',
                padding: '16px 24px',
                margin: '16px auto',
                maxWidth: '400px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid var(--cares-line-card)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  marginBottom: '8px'
                }}>
                  <FileText style={{ width: '24px', height: '24px', color: 'var(--cares-blue)' }} />
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--cares-text-1)',
                    maxWidth: '250px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {uploadedFile.name}
                  </span>
                </div>
                <p style={{
                  fontSize: '13px',
                  color: 'var(--cares-text-label)',
                  margin: 0
                }}>
                  Size: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>

              {processingStatus && (
                <div style={{
                  marginTop: '20px',
                  padding: '12px 20px',
                  background: isProcessing ? 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))' : 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))',
                  borderRadius: '10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  {isProcessing ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid color-mix(in srgb, var(--cares-blue) 30%, var(--cares-surface-card))',
                        borderTopColor: 'var(--cares-blue)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <span style={{ fontSize: '14px', color: 'var(--cares-blue)', fontWeight: '600' }}>
                        {processingStatus}
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle style={{ width: '16px', height: '16px', color: 'var(--cares-green)' }} />
                      <span style={{ fontSize: '14px', color: 'var(--cares-green)', fontWeight: '600' }}>
                        {processingStatus}
                      </span>
                    </>
                  )}
                </div>
              )}

              <button
                onClick={() => setUploadedFile(null)}
                style={{
                  marginTop: '20px',
                  padding: '10px 24px',
                  background: 'var(--cares-surface-card)',
                  color: 'var(--cares-blue)',
                  border: '2px solid var(--cares-blue)',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--cares-blue)';
                  e.target.style.color = 'var(--cares-text-max)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'var(--cares-surface-card)';
                  e.target.style.color = 'var(--cares-blue)';
                }}
              >
                🔄 Upload Different File
              </button>
            </div>
          ) : (
            <>
              {/* Upload State */}
              <div style={{
                width: '100px',
                height: '100px',
                background: dragActive
                  ? 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)'
                  : 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                boxShadow: dragActive
                  ? '0 12px 28px rgba(59, 130, 246, 0.4)'
                  : '0 8px 20px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease',
                transform: dragActive ? 'scale(1.1)' : 'scale(1)'
              }}>
                <UploadCloud style={{
                  width: '50px',
                  height: '50px',
                  color: 'var(--cares-text-max)',
                  animation: dragActive ? 'bounce 0.6s ease-in-out infinite' : 'none'
                }} />
              </div>

              <h3 style={{
                fontSize: '22px',
                fontWeight: '700',
                color: 'var(--cares-text-1)',
                marginBottom: '8px'
              }}>
                {dragActive ? '📥 Drop Your File Here!' : '📤 Upload Your Document'}
              </h3>

              <p style={{
                fontSize: '15px',
                color: 'var(--cares-text-label)',
                marginBottom: '24px',
                maxWidth: '400px',
                lineHeight: '1.6'
              }}>
                {dragActive
                  ? 'Release to upload your file'
                  : 'Drag and drop your document here, or click the button below to browse'}
              </p>

              <button
                type="button"
                onClick={triggerFileInput}
                style={{
                  background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
                  color: 'var(--cares-text-max)',
                  padding: '14px 32px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '700',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.3s',
                  marginBottom: '16px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                }}
              >
                <UploadCloud style={{ width: '20px', height: '20px' }} />
                Browse Files
              </button>

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '10px',
                border: '1px solid var(--cares-line-card)'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--cares-text-label)'
                }}>
                  <span style={{
                    padding: '4px 8px',
                    background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                    borderRadius: '6px',
                    color: 'var(--cares-blue)'
                  }}>PDF</span>
                  <span style={{
                    padding: '4px 8px',
                    background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                    borderRadius: '6px',
                    color: 'var(--cares-blue)'
                  }}>DOC</span>
                  <span style={{
                    padding: '4px 8px',
                    background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                    borderRadius: '6px',
                    color: 'var(--cares-blue)'
                  }}>DOCX</span>
                  <span style={{
                    padding: '4px 8px',
                    background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                    borderRadius: '6px',
                    color: 'var(--cares-blue)'
                  }}>JPG</span>
                  <span style={{
                    padding: '4px 8px',
                    background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                    borderRadius: '6px',
                    color: 'var(--cares-blue)'
                  }}>PNG</span>
                </div>
                <span style={{
                  fontSize: '12px',
                  color: 'var(--cares-text-note)',
                  marginLeft: '4px'
                }}>
                  • Max {MAX_FILE_SIZE_MB}MB
                </span>
              </div>
            </>
          )}

          {errors.file && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
              border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--cares-orange)'
            }}>
              <AlertCircle style={{ width: '16px', height: '16px' }} />
              <span style={{ fontSize: '14px', fontWeight: '600' }}>{errors.file}</span>
            </div>
          )}
        </div>

        {/* Add CSS animations */}
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes scaleIn {
            from { transform: scale(0); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `}</style>
      </div>
    </div>
  );

const VisionModal = ({ onClose }) => {
  const [visionData, setVisionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVisionData = async () => {
      try {
        const response = await fetch(`/api/mission/mission`);
        if (!response.ok) {
          throw new Error('Failed to fetch vision and mission data');
        }
        const data = await response.json();
        setVisionData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVisionData();
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      backdropFilter: 'blur(4px)',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '800px',
        maxHeight: '85vh',
        background: 'var(--cares-surface-card)',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
          padding: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--cares-line-card)'
        }}>
          <div>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--cares-text-max)',
              margin: 0,
              marginBottom: '8px'
            }}>
              Our Mission
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.9)',
              margin: 0
            }}>
              Guiding principles that drive our innovation
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'var(--cares-text-max)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <X style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '32px',
          overflowY: 'auto',
          flex: 1
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid var(--cares-line-card)',
                borderTop: '4px solid var(--cares-purple)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          ) : error ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: 'var(--cares-orange)',
              fontSize: '16px'
            }}>
              <AlertCircle style={{ width: '48px', height: '48px', margin: '0 auto 16px' }} />
              <p style={{ margin: 0 }}>Error: {error}</p>
            </div>
          ) : visionData ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {Object.entries(visionData)
                .filter(([key]) => key.startsWith('mission_'))
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, value], index) => (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      gap: '16px',
                      alignItems: 'flex-start',
                      padding: '20px',
                      background: index % 2 === 0 ? 'var(--cares-surface-board)' : 'var(--cares-surface-card)',
                      borderRadius: '12px',
                      border: '1px solid var(--cares-line-card)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                      color: 'var(--cares-text-max)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: '700',
                      flexShrink: 0
                    }}>
                      {index + 1}
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: '16px',
                      lineHeight: '1.7',
                      color: 'var(--cares-text-3)',
                      flex: 1
                    }}>
                      {value}
                    </p>
                  </div>
                ))}
            </div>
          ) : (
            <div style={{
              padding: '60px 20px',
              textAlign: 'center',
              color: 'var(--cares-text-label)',
              fontSize: '16px'
            }}>
              <p style={{ margin: 0 }}>No mission data available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 32px',
          borderTop: '1px solid var(--cares-line-card)',
          background: 'var(--cares-surface-board)',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 32px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
              color: 'var(--cares-text-max)',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
        {/* Professional Header with Breadcrumbs */}
        <IdeasPageHeader
          title="Idea Submission"
          description="AI-powered document analysis and form auto-population"
          breadcrumbPath="Idea Submission"
        >
          <button
            onClick={() => setShowVisionModal(true)}
            style={{
              color: 'var(--cares-blue)',
              fontWeight: '600',
              fontSize: '14px',
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 16px',
              borderRadius: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))';
              e.target.style.color = 'var(--cares-blue)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
              e.target.style.color = 'var(--cares-blue)';
            }}
          >
            📖 View Our Vision & Mission
          </button>
        </IdeasPageHeader>

        {/* Initial Selection Screen */}
        {!userSelection ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 24px',
            minHeight: '500px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--cares-text-1)',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              How would you like to submit your idea?
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--cares-text-label)',
              marginBottom: '48px',
              textAlign: 'center',
              maxWidth: '600px'
            }}>
              Choose your preferred method to get started. You can upload an existing document or enter details manually.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '32px',
              width: '100%',
              maxWidth: '900px'
            }}>
              <button
                onClick={() => setUserSelection('upload')}
                style={{
                  background: 'var(--cares-surface-card)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
                  border: '2px solid var(--cares-line-card)',
                  padding: '40px 32px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--cares-blue)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(59, 130, 246, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--cares-line-card)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px'
                }}>
                  <UploadCloud style={{ width: '40px', height: '40px', color: 'var(--cares-text-max)' }} />
                </div>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--cares-text-1)', marginBottom: '12px' }}>
                  Upload Document
                </h3>
                <p style={{ fontSize: '15px', color: 'var(--cares-text-label)', lineHeight: '1.6' }}>
                  Upload an existing document and let AI auto-populate the form fields for you
                </p>
              </button>

              <button
                onClick={() => setUserSelection('manual')}
                style={{
                  background: 'var(--cares-surface-card)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
                  border: '2px solid var(--cares-line-card)',
                  padding: '40px 32px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--cares-green)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(16, 185, 129, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--cares-line-card)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px'
                }}>
                  <Edit3 style={{ width: '40px', height: '40px', color: 'var(--cares-text-max)' }} />
                </div>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--cares-text-1)', marginBottom: '12px' }}>
                  Manual Entry
                </h3>
                <p style={{ fontSize: '15px', color: 'var(--cares-text-label)', lineHeight: '1.6' }}>
                  Fill out all fields manually with your idea details and supporting information
                </p>
              </button>
            </div>
          </div>
        ) : (
          /* Main Content After Selection */
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '24px',
            marginTop: '24px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
              {/* Left Column - Either Upload or Form Fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {userSelection === 'upload' ? (
                  <>
                    {renderUploadSection()}
                    {uploadedFile && !isProcessing && renderFormFields()}
                  </>
                ) : (
                  renderFormFields()
                )}
              </div>

              {/* Right Column - Questionnaire */}
              <div style={{ position: 'sticky', top: '24px', alignSelf: 'start' }}>
                {renderQuestionnaire()}
              </div>
            </div>
          </div>
        )}

        {/* Vision and Mission Modal */}
        {showVisionModal && <VisionModal onClose={() => setShowVisionModal(false)} />}

        {/* Processing Modal Overlay */}
        {isProcessing && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              backdropFilter: 'blur(4px)'
            }}
          >
            <div
              style={{
                background: 'var(--cares-surface-card)',
                padding: '32px',
                borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                maxWidth: '500px',
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto 24px',
                  border: '4px solid var(--cares-line-card)',
                  borderTop: '4px solid var(--cares-blue)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              ></div>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'var(--cares-text-2)',
                  marginBottom: '12px'
                }}
              >
                Processing Your Idea
              </div>
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--cares-text-label)',
                  lineHeight: '1.6'
                }}
              >
                Submitting idea and running AI reviews...<br />
                This may take 1-2 minutes. Please wait.
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {showToast && (
          <div
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
              color: 'var(--cares-text-max)',
              padding: '16px 24px',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              zIndex: 9999,
              animation: 'slideInUp 0.3s ease-out',
              minWidth: '320px'
            }}
          >
            <CheckCircle style={{ width: '24px', height: '24px' }} />
            <div>
              <div style={{ fontWeight: '600', fontSize: '16px' }}>Success!</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>
                Idea submitted successfully. Redirecting to Ideas Management...
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoScribeComponent;